import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CreditCard, MapPin, Pencil, PlusCircle, Server, Trash2, X } from "lucide-react";
import React, { useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { Btn, Chip, Confirm, Field, FilePickButton, IconBtn, MediaViewer, Modal, Page, cx } from "../components/wf";
import { api, fileUrl, uploadFile } from "../lib/api";
import { useRefreshJobs } from "../lib/queries";

const TYPES = [
  { value: "reader", label: "Reader", icon: CreditCard, color: "var(--wf-brand)" },
  { value: "panel", label: "Panel", icon: Server, color: "var(--wf-err)" },
  { value: "other", label: "Other", icon: MapPin, color: "var(--wf-warn)" },
];
const colorOf = (t) => TYPES.find((x) => x.value === t)?.color ?? "var(--wf-warn)";
const clamp = (v) => Math.min(1, Math.max(0, v));

function PinMarker({ p, n, focused, onPointerDown }) {
  return (
    <button
      type="button"
      data-testid={`floor-plan-pin-${p.id}`}
      title={p.label || p.type}
      onPointerDown={onPointerDown}
      style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}
      className={cx("absolute z-10 -ml-[14px] -mt-[34px] flex cursor-grab touch-none flex-col items-center active:cursor-grabbing", focused && "z-20 scale-125")}
    >
      <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white text-xs font-extrabold text-white shadow-md" style={{ background: colorOf(p.type) }}>{n}</span>
      <span className="-mt-0.5 h-0 w-0 border-x-[6px] border-t-[8px] border-x-transparent" style={{ borderTopColor: colorOf(p.type) }} />
    </button>
  );
}

function PinDialog({ draft, setDraft, saving, onSave, onDelete, onView, viewing }) {
  const [uploading, setUploading] = useState(false);
  const addPhotos = async (files) => {
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) paths.push(await uploadFile(f));
      setDraft((d) => ({ ...d, photos: [...(d.photos ?? []), ...paths] }));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };
  return (
    <Modal open={!!draft} onClose={() => setDraft(null)} title={draft?.id ? "Edit Pin" : "New Pin"} testid="pin-sheet" lockOutside={viewing}>
      <div className="flex gap-2">
        {TYPES.map((t) => <Chip key={t.value} label={t.label} selected={draft?.type === t.value} onClick={() => setDraft((d) => ({ ...d, type: t.value }))} testid={`pin-type-${t.value}`} />)}
      </div>
      <Field label="Label" testid="pin-label-input" value={draft?.label} onChange={(v) => setDraft((d) => ({ ...d, label: v }))} placeholder="Front door reader, MDF panel…" />
      <Field label="Note" testid="pin-note-input" value={draft?.note} onChange={(v) => setDraft((d) => ({ ...d, note: v }))} multiline />
      <p className="text-[13px] font-semibold text-wf-ink2">Pictures ({draft?.photos?.length ?? 0})</p>
      <div className="flex flex-wrap gap-2">
        {(draft?.photos ?? []).map((ph, k) => (
          <div key={ph} className="group relative">
            <button type="button" onClick={() => onView(draft.photos, ph)} data-testid={`pin-draft-photo-open-${k}`}><img src={fileUrl(ph)} alt="" className="h-16 w-16 rounded-lg bg-wf-s3 object-cover" /></button>
            <button type="button" data-testid="pin-photo-remove" onClick={() => setDraft((d) => ({ ...d, photos: d.photos.filter((x) => x !== ph) }))}
              className="absolute -right-1.5 -top-1.5 rounded-full bg-wf-err p-0.5 text-white"><X size={12} /></button>
          </div>
        ))}
        <FilePickButton testid="pin-add-photo" accept="image/*" multiple loading={uploading} onFiles={addPhotos} className="!h-16 !w-16 !px-0">{uploading ? "" : "Add"}</FilePickButton>
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        {draft?.id && <Btn variant="ghost" icon={Trash2} onClick={onDelete} data-testid="pin-delete-button" className="mr-auto !text-wf-err">Remove Pin</Btn>}
        <Btn variant="ghost" onClick={() => setDraft(null)} data-testid="pin-cancel-button">Cancel</Btn>
        <Btn loading={saving} onClick={onSave} data-testid="pin-save-button">{draft?.id ? "Save Pin" : "Add Pin"}</Btn>
      </div>
    </Modal>
  );
}

/** One floor plan image with reader / panel / other pins — click to drop, drag to move, click a pin to edit. */
export default function FloorPlan() {
  const { wid } = useParams();
  const path = useSearchParams()[0].get("path") ?? "";
  const nav = useNavigate();
  const qc = useQueryClient();
  const refresh = useRefreshJobs();
  const key = ["floor-plan-pins", wid];
  const q = useQuery({ queryKey: key, queryFn: () => api(`/work-orders/${wid}/floor-plan-pins`) });
  const pins = (q.data ?? []).filter((p) => p.path === path);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [focus, setFocus] = useState(null);
  const [drag, setDrag] = useState(null);
  const [removePlan, setRemovePlan] = useState(false);
  const [removePin, setRemovePin] = useState(false);
  const canvas = useRef(null);

  const pointAt = (e) => {
    const r = canvas.current.getBoundingClientRect();
    return { x: clamp((e.clientX - r.left) / r.width), y: clamp((e.clientY - r.top) / r.height) };
  };
  const send = async (pinId, body) => {
    const res = await api(`/work-orders/${wid}/floor-plan-pins${pinId ? `/${pinId}` : ""}`, { method: pinId ? "PUT" : "POST", body });
    qc.setQueryData(key, res);
    qc.invalidateQueries({ queryKey: ["work-order", wid] });
  };
  const bodyOf = (p) => ({ path, x: p.x, y: p.y, type: p.type, label: (p.label ?? "").trim(), note: (p.note ?? "").trim(), photos: p.photos ?? [] });
  const [pinViewer, setPinViewer] = useState(null);

  const save = async () => {
    setSaving(true);
    try {
      await send(draft.id, bodyOf(draft));
      toast.success(draft.id ? "Pin updated" : "Pin added");
      setDraft(null);
      setAdding(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    try {
      qc.setQueryData(key, await api(`/work-orders/${wid}/floor-plan-pins/${draft.id}`, { method: "DELETE" }));
      qc.invalidateQueries({ queryKey: ["work-order", wid] });
      toast.success("Pin removed");
      setDraft(null);
    } catch (e) {
      toast.error(e.message);
    }
  };
  const startDrag = (e, p) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag({ pin: p, x: p.x, y: p.y, sx: e.clientX, sy: e.clientY, moved: false });
  };
  const onMove = (e) => {
    if (!drag) return;
    const moved = drag.moved || Math.hypot(e.clientX - drag.sx, e.clientY - drag.sy) > 4;
    setDrag({ ...drag, ...(moved ? pointAt(e) : {}), moved });
  };
  const onUp = async () => {
    if (!drag) return;
    const d = drag;
    setDrag(null);
    if (!d.moved) return setDraft({ ...d.pin, photos: d.pin.photos ?? [] });
    qc.setQueryData(key, (all) => all.map((x) => (x.id === d.pin.id ? { ...x, x: d.x, y: d.y } : x)));
    try {
      await send(d.pin.id, bodyOf({ ...d.pin, x: d.x, y: d.y }));
      toast.success("Pin moved");
    } catch (e) {
      toast.error(e.message);
      q.refetch();
    }
  };
  const deletePlan = async () => {
    try {
      const wo = await api(`/work-orders/${wid}`);
      const body = {
        customer_id: wo.customer_id, job_description: wo.job_description, description_details: wo.description_details, hours_availability: wo.hours_availability,
        status: wo.status, job_notes: wo.job_notes, job_photos: wo.job_photos, drive_link: wo.drive_link, budget: wo.budget, assigned_to: wo.assigned_to ?? [],
        floor_plans: (wo.floor_plans ?? []).filter((x) => x !== path),
      };
      refresh(await api(`/work-orders/${wid}`, { method: "PUT", body }));
      toast.success("Floor plan removed");
      nav(-1);
    } catch (e) {
      toast.error(e.message);
    }
  };
  const shown = pins.map((p) => (drag?.pin.id === p.id ? { ...p, x: drag.x, y: drag.y } : p));

  return (
    <Page title="Floor Plan" subtitle={`${pins.length} pin${pins.length === 1 ? "" : "s"} · drag a pin to move it`} back wide testid="floor-plan-page"
      right={<>
        <IconBtn icon={Trash2} tone="danger" onClick={() => setRemovePlan(true)} data-testid="floor-plan-remove" title="Remove floor plan" />
        <Btn variant={adding ? "primary" : "secondary"} icon={adding ? X : PlusCircle} onClick={() => setAdding((a) => !a)} data-testid="floor-plan-add-pin">
          {adding ? "Click the plan to drop a pin" : "Add Pin"}
        </Btn>
      </>}>
      <div className="grid gap-6 xl:grid-cols-[1fr_320px]">
        <div
          ref={canvas}
          data-testid="floor-plan-canvas"
          onClick={(e) => adding && !drag && !e.target.closest("button") && setDraft({ ...pointAt(e), type: "reader", label: "", note: "", photos: [] })}
          onPointerMove={onMove}
          onPointerUp={onUp}
          className={cx("relative select-none overflow-hidden rounded-xl border-2 bg-wf-s3", adding ? "cursor-crosshair border-wf-brand" : "border-wf-line")}
        >
          <img src={fileUrl(path)} alt="Floor plan" draggable={false} className="pointer-events-none block w-full" data-testid="floor-plan-image" />
          {shown.map((p, i) => <PinMarker key={p.id} p={p} n={i + 1} focused={focus === p.id} onPointerDown={(e) => startDrag(e, p)} />)}
          {draft && !draft.id && <PinMarker p={{ ...draft, id: "draft" }} n="+" />}
        </div>
        <div>
          <div className="mb-3 flex flex-wrap gap-4" data-testid="floor-plan-legend">
            {TYPES.map((t) => (
              <span key={t.value} className="flex items-center gap-1.5 text-sm font-semibold">
                <span className="h-3 w-3 rounded-full" style={{ background: t.color }} />{t.label} ({pins.filter((p) => p.type === t.value).length})
              </span>
            ))}
          </div>
          {!pins.length ? <p className="text-sm text-wf-mute">No pins yet. Click Add Pin, then click the plan where a reader or panel goes.</p> : pins.map((p, i) => (
            <div key={p.id} data-testid={`floor-plan-pin-row-${p.id}`} onMouseEnter={() => setFocus(p.id)} onMouseLeave={() => setFocus(null)}
              className="mb-2 flex items-center gap-3 rounded-xl border p-3 transition-colors" style={{ borderColor: focus === p.id ? colorOf(p.type) : "var(--wf-line)" }}>
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-extrabold text-white" style={{ background: colorOf(p.type) }}>{i + 1}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold">{p.label || TYPES.find((t) => t.value === p.type)?.label}</p>
                {p.note && <p className="truncate text-sm text-wf-mute">{p.note}</p>}
                {p.created_by && <p className="text-[11px] text-wf-mute">By {p.created_by}</p>}
                {!!p.photos?.length && (
                  <div className="mt-1 flex gap-1" data-testid={`floor-plan-pin-photos-${p.id}`}>
                    {p.photos.slice(0, 4).map((ph, k) => (
                      <button key={ph} data-testid={`pin-photo-open-${p.id}-${k}`} onClick={() => setPinViewer({ list: p.photos, path: ph })}>
                        <img src={fileUrl(ph)} alt="" className="h-10 w-10 rounded-md bg-wf-s3 object-cover" />
                      </button>
                    ))}
                    {p.photos.length > 4 && <span className="self-center text-xs text-wf-mute">+{p.photos.length - 4}</span>}
                  </div>
                )}
              </div>
              <IconBtn icon={Pencil} onClick={() => setDraft({ ...p, photos: p.photos ?? [] })} data-testid={`floor-plan-pin-edit-${p.id}`} title="Edit pin" />
            </div>
          ))}
        </div>
      </div>
      <PinDialog draft={draft} setDraft={setDraft} saving={saving} onSave={save} onDelete={() => setRemovePin(true)} onView={(list, ph) => setPinViewer({ list, path: ph })} viewing={!!pinViewer} />
      <MediaViewer path={pinViewer?.path} list={pinViewer?.list} onChange={(ph) => setPinViewer((v) => ({ ...v, path: ph }))} onClose={() => setPinViewer(null)} />
      <Confirm open={removePin} title="Remove this pin?" confirmLabel="Remove" onConfirm={remove} onClose={() => setRemovePin(false)} />
      <Confirm open={removePlan} title="Remove this floor plan?" message="The image and its pins are removed from this job." confirmLabel="Remove" onConfirm={deletePlan} onClose={() => setRemovePlan(false)} />
    </Page>
  );
}
